/** @jsxImportSource @emotion/react */
import { useMemo, useState, useEffect } from 'react'
import styled from '@emotion/styled'
import { Button } from 'react-bootstrap'
import { Formik, Form, Field } from 'formik'
import * as Yup from 'yup'
import Toast from 'components/atoms/Toast'

import { confirm } from 'components/molecules/Confirmation'
import { phoneNumberMask } from 'helpers/masks'
import MaskInput from 'components/atoms/MaskInput'
import SearchableInput from 'components/atoms/SearchableInput'
import { CompanyCollections } from './CompanyUpsert'
import { KeyPerson } from 'ts-back-end/src/business/company/dto/company.dto'
import { requiredPhoneValidation } from 'helpers/validate'
import { KeyPersonRegionsField } from './KeyPersonRegionsField'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const CardContainer = styled.div`
  display: block;
`

const CardItem = styled.div`
  background: white;
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 12px;
  margin-bottom: 12px;
  width: 100%;
  box-sizing: border-box;
`

const CardRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }
`

const CardRowTwoColumns = styled.div`
  display: flex;
  gap: 16px;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
    margin-bottom: 0;
    padding-bottom: 0;
  }

  @media (max-width: 576px) {
    flex-direction: column;
    gap: 8px;
  }
`

const CardField = styled.div`
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  min-width: 0;
`

const CardLabel = styled.span`
  font-weight: 600;
  color: #6d757e;
  min-width: 80px;
  margin-right: 12px;
  flex-shrink: 0;
`

const CardValue = styled.span`
  flex: 1;
  text-align: right;
  word-break: break-word;
  min-width: 0;
`

const CardActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
`

const validation = Yup.object().shape({
  fullName: Yup.string().required('Required'),
  title: Yup.string().required('Required'),
  telephone: requiredPhoneValidation,
  email: Yup.string().email('Invalid Email'),
})

interface IProps {
  keyPeople: KeyPerson[] | null
  regions: { regionName: string; uuid: string }[]
  onUpdateCompanyCollection: (data: Partial<CompanyCollections>) => void
}

type FormValues = {
  fullName: string
  title: string
  telephone: string
  email: string
  regions: string[]
  regionToAdd: string
}

const getEmptyValues = (): FormValues => ({
  fullName: '',
  title: '',
  telephone: '',
  email: '',
  regions: [],
  regionToAdd: '',
})

export const KeyPeople = ({ keyPeople, regions, onUpdateCompanyCollection }: IProps) => {
  const regionMap = useMemo(
    () => new Map(regions.map((region) => [region.uuid, region.regionName])),
    [regions],
  )
  const titleOptions = useMemo(() => {
    const seen = new Set<string>()
    return (
      keyPeople
        ?.map((person) => person.title)
        .filter((title): title is string => Boolean(title?.trim()))
        .filter((title) => {
          if (seen.has(title!)) return false
          seen.add(title!)
          return true
        })
        .map((title) => ({ label: title, value: title })) || []
    )
  }, [keyPeople])
  const [editingIdx, setEditingIdx] = useState<number | null>(null)
  const [formInitialValues, setFormInitialValues] = useState<FormValues>(getEmptyValues())

  useEffect(() => {
    if (editingIdx !== null && (!keyPeople || !keyPeople[editingIdx])) {
      setEditingIdx(null)
      setFormInitialValues(getEmptyValues())
    }
  }, [editingIdx, keyPeople])

  const formatRegions = (personRegions?: string[]) => {
    if (!personRegions?.length) {
      return '—'
    }

    const names = personRegions.map((regionId) => regionMap.get(regionId) ?? regionId)
    return names.join(', ')
  }

  return (
    <Container>
      <div className="d-flex w-100">
        <Formik
          enableReinitialize={true}
          validationSchema={validation}
          initialValues={formInitialValues}
          onSubmit={async (values, { resetForm }) => {
            try {
              const newEntry = {
                fullName: values.fullName,
                title: values.title,
                telephone: values.telephone,
                email: values.email,
                regions: values.regions,
              }

              const updatedKeyPersons =
                editingIdx === null
                  ? [...(keyPeople || []), newEntry]
                  : (keyPeople || []).map((person, idx) => (idx === editingIdx ? newEntry : person))

              await onUpdateCompanyCollection({ keyPersons: updatedKeyPersons })
              setEditingIdx(null)
              const resetValues = getEmptyValues()
              setFormInitialValues(resetValues)
              resetForm({ values: resetValues })
              Toast({ type: 'success', label: `Key people updated!` })
            } catch (err) {
              Toast({ type: 'error', label: err.data.message })
            }
          }}>
          {({
            values,
            errors,
            touched,
            handleChange,
            setFieldValue,
            setFieldTouched,
            resetForm,
          }) => (
            <Form id="keyPeople" className="w-100">
              <div className={`container bg-white p-0`}>
                <div className={`container pt-3 pb-2`}>
                  <div className="row">
                    <div className="col-12 col-md-4 form-group textGray">
                      <label htmlFor="name">
                        Full Name
                        <span className="text-danger">*</span>
                      </label>
                      <Field
                        type="text"
                        name="fullName"
                        className="form-control"
                        placeholder="Full Name"
                        value={values.fullName}
                        onChange={handleChange}
                      />
                      {touched.fullName && errors.fullName && (
                        <div className="text-danger">{errors.fullName}</div>
                      )}
                    </div>
                    <div className="col-12 col-md-4 form-group textGray">
                      <label htmlFor="title">
                        Title <span className="text-danger">*</span>
                      </label>
                      <SearchableInput
                        value={values.title}
                        options={titleOptions}
                        placeholder="Title"
                        onChange={(val) => setFieldValue('title', val)}
                        onBlur={() => setFieldTouched('title', true)}
                      />
                      {touched.title && errors.title && (
                        <div className="text-danger">{errors.title}</div>
                      )}
                    </div>
                    <div className="col-12 col-md-4 form-group textGray">
                      <label htmlFor="email">Email</label>
                      <Field
                        type="email"
                        name="email"
                        className="form-control"
                        placeholder="Email"
                        value={values.email}
                        onChange={handleChange}
                      />
                      {touched.email && errors.email && (
                        <div className="text-danger">{errors.email}</div>
                      )}
                    </div>
                  </div>
                  <div className="row" css={{ alignItems: 'flex-end' }}>
                    <div className="col-12 col-md-6 form-group textGray">
                      <label htmlFor="telephone">
                        Telephone <span className="text-danger">*</span>
                      </label>
                      <MaskInput
                        value={values.telephone}
                        onChange={handleChange}
                        name="telephone"
                        mask={phoneNumberMask}
                        placeholder="(###) ###-####"
                        autoComplete="off"
                      />
                      {touched.telephone && errors.telephone && (
                        <div className="text-danger">{errors.telephone}</div>
                      )}
                    </div>
                    <div className="col-12 col-md-6 form-group textGray">
                      {editingIdx === null ? (
                        <Button variant="primary" type="submit" form="keyPeople">
                          Add
                        </Button>
                      ) : (
                        <div className="d-flex">
                          <Button
                            variant="outline-primary"
                            className="mr-2"
                            type="button"
                            onClick={() => {
                              setEditingIdx(null)
                              const resetValues = getEmptyValues()
                              setFormInitialValues(resetValues)
                              resetForm({ values: resetValues })
                            }}>
                            Cancel
                          </Button>
                          <Button variant="primary" type="submit" form="keyPeople">
                            Save
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                  <KeyPersonRegionsField
                    availableRegions={regions}
                    regionMap={regionMap}
                    selectedRegions={values.regions}
                    regionToAdd={values.regionToAdd}
                    setFieldValue={setFieldValue}
                  />
                </div>
              </div>
            </Form>
          )}
        </Formik>
      </div>
      {keyPeople?.length ? (
        <>
          {keyPeople.map((person, idx) => (
            <CardContainer key={`${person.fullName}-${person.title}-${idx}`}>
              <CardItem>
                <CardRowTwoColumns>
                  <CardField>
                    <CardLabel>Full Name:</CardLabel>
                    <CardValue>{person.fullName}</CardValue>
                  </CardField>
                  <CardField>
                    <CardLabel>Title:</CardLabel>
                    <CardValue>{person.title}</CardValue>
                  </CardField>
                </CardRowTwoColumns>
                <CardRowTwoColumns>
                  <CardField>
                    <CardLabel>Email:</CardLabel>
                    <CardValue>{person.email || '—'}</CardValue>
                  </CardField>
                  <CardField>
                    <CardLabel>Telephone:</CardLabel>
                    <CardValue>{person.telephone}</CardValue>
                  </CardField>
                </CardRowTwoColumns>
                <CardRow>
                  <CardLabel>Regions:</CardLabel>
                  <CardValue>{formatRegions(person.regions)}</CardValue>
                </CardRow>
                <CardActions>
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={() => {
                      setEditingIdx(idx)
                      setFormInitialValues({
                        fullName: person.fullName || '',
                        title: person.title || '',
                        telephone: person.telephone || '',
                        email: person.email || '',
                        regions: person.regions || [],
                        regionToAdd: '',
                      })
                    }}>
                    Edit
                  </Button>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() =>
                      confirm('contact')
                        .then(() =>
                          onUpdateCompanyCollection({
                            keyPersons: keyPeople.filter((_, keyIdx) => keyIdx !== idx),
                          }),
                        )
                        .catch(() => {})
                    }>
                    Remove
                  </Button>
                </CardActions>
              </CardItem>
            </CardContainer>
          ))}
        </>
      ) : (
        <div className="row my-1 mt-3">
          <div className="col-sm-12 p-0">
            <div className="list-group text-secondary">
              <div className="list-group-item list-group-item-action flex-column align-items-center">
                <div className="d-flex py-1 justify-content-center align-items-center">
                  <span>No contacts provided</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  )
}
